import { useEffect, useRef, useState } from "react";

import { Bot, Brain, Eye, Globe, Search, Shield, Sparkles, Zap } from "lucide-react";

const WITTY_MESSAGES = [
  { text: "Asking ChatGPT if it even knows your business exists...", icon: Search },
  { text: "Gemini is thinking really hard about your question...", icon: Brain },
  { text: "Claude is quietly forming an opinion about you...", icon: Bot },
  { text: "Checking if AI search engines play favorites...", icon: Eye },
  { text: "Counting how many times your business gets name-dropped...", icon: Sparkles },
  { text: "Three AIs walk into a room and start comparing notes...", icon: Globe },
  { text: "Ranking results faster than you can refresh the page...", icon: Zap },
  { text: "Convincing three AIs to agree on something \u2014 wish us luck...", icon: Shield },
  { text: "Our AI panel is deliberating your business's fate...", icon: Brain },
  { text: "Running the world's most expensive recommendation engine...", icon: Search },
];

const LLM_BADGES = [
  { name: "ChatGPT", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  { name: "Gemini", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  { name: "Claude", color: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
];

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const VisibilityLoaderDialog = ({ query }) => {
  const [elapsed, setElapsed] = useState(0);
  const [msgIndex, setMsgIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % WITTY_MESSAGES.length);
        setIsTransitioning(false);
      }, 400);
    }, 10000);
    return () => clearInterval(interval);
  }, [msgIndex]);

  const currentMsg = WITTY_MESSAGES[msgIndex];
  const CurrentIcon = currentMsg.icon;

  return (
    <div className="audit-loader-overlay">
      <div className="audit-loader-card">
        {/* Spinner */}
        <div className="audit-spinner-container">
          <div className="audit-spinner-ring audit-ring-1" />
          <div className="audit-spinner-ring audit-ring-2" />
          <div className="audit-spinner-ring audit-ring-3" />
          <div className="audit-spinner-core">
            <div className="flex gap-1">
              {LLM_BADGES.map((b) => (
                <span
                  key={b.name}
                  className={`text-[8px] font-bold px-1 py-0.5 rounded border ${b.color}`}
                >
                  {b.name.charAt(0)}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Phase badge */}
        <div className="audit-phase-badge">
          <span className="audit-phase-dot" />
          Querying 3 LLM Providers
        </div>

        <h2 className="audit-loader-title">Checking Your Business's Visibility Across AI Search</h2>
        <p className="audit-loader-subtitle">
          We're asking ChatGPT, Gemini and Claude your exact question and analyzing their responses
        </p>

        {/* LLM Provider Badges */}
        <div className="flex items-center justify-center gap-2 mt-2 mb-2">
          {LLM_BADGES.map((badge) => (
            <span
              key={badge.name}
              className={`text-xs font-semibold px-3 py-1 rounded-full border ${badge.color}`}
            >
              {badge.name}
            </span>
          ))}
        </div>

        {/* Query chip */}
        <div className="audit-url-chip">
          <Search className="w-4 h-4 text-primary shrink-0" />
          <span className="truncate">{query}</span>
        </div>

        {/* Cycling witty message */}
        <div className="audit-witty-container">
          <div
            className={`audit-witty-msg ${isTransitioning ? "audit-witty-exit" : "audit-witty-enter"}`}
          >
            <CurrentIcon className="w-4 h-4 text-primary shrink-0" />
            <span>{currentMsg.text}</span>
          </div>
        </div>

        {/* Progress dots */}
        <div className="audit-progress-dots">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="audit-dot" style={{ animationDelay: `${i * 0.2}s` }} />
          ))}
        </div>

        {/* Elapsed */}
        <div className="audit-elapsed">
          <span className="audit-elapsed-label">Elapsed</span>
          <span className="audit-elapsed-time">{formatTime(elapsed)}</span>
        </div>

        {/* ETA */}
        <div className="text-xs text-on-surface-variant mt-2 font-medium">
          Estimated time: <span className="text-primary">5-15 seconds</span>
        </div>
      </div>
    </div>
  );
};

export default VisibilityLoaderDialog;
